import {
  Link,
  createFileRoute,
  notFound,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import { HTTPError } from "ky";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { UsersTabContent } from "./-tabs/UsersTab";
import { PoliciesTabContent } from "./-tabs/PoliciesTab";
import { LlmApiKeysTabContent } from "./-tabs/LlmApiKeysTab";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";
import {
  groupPoliciesQueryOptions,
  groupQueryOptions,
  groupUsersListQueryOptions,
} from "@/lib/queries/groups";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

const tabs = ["Users", "Policies", "LLM API Keys"] as const;
const searchSchema = z.object({
  tab: z.enum(tabs).catch("Users").default("Users"),
  page: z.number().int().positive().default(1),
  per_page: z.number().int().positive().max(100).default(10),
  search: z.string().optional(),
});

const pathParamsSchema = z.object({
  id: z.uuid(),
});

export const Route = createFileRoute("/_app/groups_/$id")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  params: {
    parse: pathParamsSchema.parse,
  },
  search: {
    middlewares: [stripSearchParams({ search: undefined })],
  },
  loaderDeps: ({ search }) => search,
  staticData: {
    permissionGuards: [
      {
        resource: "groups",
      },
    ],
  },
  async loader({ context: { queryClient }, deps, params: { id } }) {
    try {
      await Promise.race([
        queryClient.ensureQueryData(groupQueryOptions(id)),
        queryClient.ensureQueryData(
          groupUsersListQueryOptions(id, {
            page: deps.page,
            per_page: deps.per_page,
          }),
        ),
        queryClient.ensureQueryData(groupPoliciesQueryOptions(id)),
      ]);
    } catch (e) {
      if (!(e instanceof HTTPError)) throw e;
      if (e.response.status === 404) throw notFound();
    }
  },
  pendingComponent: () => (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-64 w-full" />
    </div>
  ),
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data: group } = useSuspenseQuery(groupQueryOptions(id));

  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const setTab = useCallback(
    (selectedTab: typeof tab) =>
      navigate({
        to: ".",
        from: Route.fullPath,
        search: (s) => ({ ...s, tab: selectedTab }),
      }),
    [navigate, Route.fullPath],
  );

  return (
    <div className="space-y-6 p-6">
      <BreadcrumbNav
        items={[
          { label: "Groups", link: <Link to="/groups">Groups</Link> },
          { label: "Group Details" },
        ]}
      />

      <Card>
        <CardContent className="w-full">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Name
                </label>
                <p className="mt-1 text-lg font-semibold">{group.name}</p>
              </div>
              {group.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Description
                  </label>
                  <p className="mt-1">{group.description}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs
        defaultValue="Users"
        className="w-full"
        value={tab}
        onValueChange={(t) => setTab(t as typeof tab)}
      >
        <TabsList className="grid w-full grid-cols-3">
          {tabs.map((t) => (
            <TabsTrigger key={t} value={t}>
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="Users" className="mt-4">
          <UsersTabContent groupId={id} />
        </TabsContent>

        <TabsContent value="Policies" className="mt-4">
          <PoliciesTabContent groupId={id} />
        </TabsContent>
        <TabsContent value="LLM API Keys" className="mt-4">
          <LlmApiKeysTabContent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
